/**
 * Copyright(c) 2024 Magrabi
 *
 * All rights reserved.
 *
 * @module mReserve Store Utils
 */
"use server";

import MReserveClient from "@/api/mReserve/client";
import {
  mapMReserveStoreToStoreType,
  mapMReserveSlotToEyeTestSlot,
  mapMReserveCustomerBookingToBookingDetails,
  mapServiceNameToEyeTestType,
} from "@/api/mReserve/dataMapper";
import {
  BookingDetailsWithServiceId,
  CreateBookingRequest,
  MReserveTimeSlot,
  RescheduleBookingRequest,
  MReserveCustomerBookingsRequest,
} from "@/api/mReserve/types";
import { BookingDetails, BookingResponse, CancelAppointmentResponse, EyeTestTimeSlot, StoreType, TypeBookingRequest } from "@/common/lib/types";

/**
 * Get stores by country (not service-based)
 */
export const getStoresByCountry = async (countryCode?: string, searchTerm: string = "", locale = "en"): Promise<StoreType[]> => {
  try {
    const client = new MReserveClient(locale);
    const params: { country_code?: string; search?: string } = {};
    if (countryCode) {
      params.country_code = countryCode;
    }
    if (searchTerm) {
      params.search = searchTerm;
    }
    const mReserveStores = await client.getStoresByCountry(params);
    return mReserveStores.map((store) => mapMReserveStoreToStoreType(store));
  } catch (error) {
    console.error("Error fetching stores by country:", error);
    return [];
  }
};

/**
 * Get stores by service ID
 */
export const getStores = async (country: string, searchTerm: string = "", serviceId?: number, locale = "en"): Promise<StoreType[]> => {
  try {
    // If serviceId is provided, get stores by service
    if (serviceId && serviceId > 0) {
      const client = new MReserveClient(locale);
      const params: { search?: string } = {};
      if (searchTerm) {
        params.search = searchTerm;
      }
      const mReserveStores = await client.getStoresByService(serviceId, params);
      return mReserveStores.map((store) => mapMReserveStoreToStoreType(store));
    }
    
    // If no serviceId, get stores by country
    return await getStoresByCountry(country, searchTerm, locale);
  } catch (error) {
    console.error("Error fetching stores:", error);
    return [];
  }
};

export const getStoreDetails = async (storeId: string, serviceId: number, locale = "en"): Promise<StoreType | undefined> => {
  try {
    const client = new MReserveClient(locale);
    const store = await client.getStoreDetails(serviceId, storeId);
    return mapMReserveStoreToStoreType(store);
  } catch (error) {
    console.error("Error fetching store details:", error);
    return undefined;
  }
};

export const getTimeSlots = async (storeId: string, date: string, serviceId?: number, locale = "en"): Promise<EyeTestTimeSlot[] | undefined> => {
  try {
    if (!serviceId) {
      return undefined;
    }

    const client = new MReserveClient(locale);
    const slotsResponse = await client.getTimeSlots(serviceId, Number(storeId), date);
    const timeSlots = slotsResponse.data?.slots || [];
    return timeSlots.map((slot: MReserveTimeSlot) => mapMReserveSlotToEyeTestSlot(slot));
  } catch (error) {
    console.error("Error fetching time slots:", error);
    return undefined;
  }
};

export const bookAppointment = async (data: TypeBookingRequest & { serviceId?: number }, locale = "en"): Promise<BookingResponse | undefined> => {
  try {
    const serviceId = data.serviceId;
    if (!serviceId || !data.storeId || !data.startDatetime || !data.user) {
      return {
        error: {
          code: "INVALID_REQUEST",
          message: "Missing required fields",
        },
      };
    }

    const client = new MReserveClient(locale);
    // Parse datetime to date and time
    // Input format: "2024-01-15 14:30:00" or "2024-01-15T14:30:00"
    const datetimeStr = data.startDatetime.replace(" ", "T");
    const datetime = new Date(datetimeStr);

    // Extract date (YYYY-MM-DD) in LOCAL timezone, not UTC
    const year = datetime.getFullYear();
    const month = String(datetime.getMonth() + 1).padStart(2, '0');
    const day = String(datetime.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;

    // Extract time (HH:mm) in LOCAL timezone
    const hours = String(datetime.getHours()).padStart(2, '0');
    const minutes = String(datetime.getMinutes()).padStart(2, '0');
    const time = `${hours}:${minutes}`;

    // Flat structure matching CreateBookingRequest type
    const bookingData: CreateBookingRequest = {
      serviceId: serviceId,
      storeId: Number(data.storeId),
      date: date,
      time: time,
      firstName: data.user.firstName || "",
      lastName: data.user.lastName || "",
      email: data.user.email || "",
      phone: data.user.phoneNumber || "",
      description: data.comment || "",
      termsAccepted: true,
      mgr_customer_id: data.mgr_customer_id,
      sfcontactkey: data.sfcontactkey,
    };

    const booking = await client.createBooking(bookingData);

    return {
      data: {
        success: true,
        id: booking.id,
      },
    };
  } catch (error) {
    console.error("Error booking appointment:", error);
    return {
      error: {
        code: "BOOKING_FAILED",
        message: error instanceof Error ? error.message : "Failed to book appointment",
      },
    };
  }
};

// Helper function to extract date from ISO string or return as-is
const extractDate = (dateString?: string): string => {
  if (!dateString) return "";
  return dateString.includes("T") ? dateString.split("T")[0] : dateString;
};

// Helper function to extract time from ISO string or return as-is
const extractTime = (timeString?: string): string => {
  if (!timeString) return "";

  if (timeString.includes("T")) {
    const timePart = timeString.split("T")[1];
    return timePart ? timePart.substring(0, 5) : "";
  }

  // Check if already in HH:mm format
  if (/^\d{2}:\d{2}$/.test(timeString)) {
    return timeString;
  }

  return timeString;
};

// Helper function to extract date and time from booking
const extractBookingDateTime = (booking: any) => {
  let bookingDate = extractDate(booking.bookingDate);
  let startTime = extractTime(booking.startTime);
  let endTime = extractTime(booking.endTime);

  // If bookingDate is in UTC ISO format, convert to local date
  if (bookingDate && booking.bookingDate?.includes("T")) {
    // Parse the UTC date and convert to local date string
    const utcDate = new Date(booking.bookingDate);
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    bookingDate = `${year}-${month}-${day}`;
  }

  // Fallback: Try to extract from slot if available
  if (!startTime) {
    startTime = extractTime(booking.slot?.startTime);
  }

  if (!endTime) {
    endTime = extractTime(booking.slot?.endTime);
  }

  // If we still don't have bookingDate, try to extract from startTime if it's a full date
  if (!bookingDate && booking.startTime?.includes("T")) {
    bookingDate = booking.startTime.split("T")[0];
  }

  // Final fallback: if bookingDate is in ISO format in slot startTime/endTime
  if (!bookingDate) {
    if (booking.slot?.startTime?.includes("T")) {
      bookingDate = booking.slot.startTime.split("T")[0];
    } else if (booking.slot?.endTime?.includes("T")) {
      bookingDate = booking.slot.endTime.split("T")[0];
    }
  }

  return { bookingDate, startTime, endTime };
};

export const getBookingDetails = async (bookingId: string, storeId: string, locale = "en"): Promise<BookingDetailsWithServiceId | undefined> => {
  try {
    const client = new MReserveClient(locale);
    const booking = await client.getBooking(bookingId);

    const serviceId = booking.serviceId ? Number(booking.serviceId) : undefined;

    const eyeTestType = mapServiceNameToEyeTestType(booking.service?.name);
    const { bookingDate, startTime, endTime } = extractBookingDateTime(booking);

    return {
      id: booking.id,
      storeId: {
        id: booking.storeId || storeId,
        name: booking.store?.name,
      },
      date: bookingDate,
      startTime: startTime,
      endTime: endTime,
      eyeTestType: eyeTestType,
      serviceTitle: booking.service?.name,
      serviceIcon: booking.service?.service_icon || null, // Add this line
      customer: booking.customer
        ? {
            firstName: booking.customer.firstName,
            lastName: booking.customer.lastName,
            email: booking.customer.email,
            phoneNumber: booking.customer.mobile,
          }
        : undefined,
      comments: booking.comments,
      bookingStatus: booking.status,
      serviceId: serviceId,
    } as BookingDetailsWithServiceId;
  } catch (error) {
    console.error("Error fetching booking details:", error);
    return undefined;
  }
};

/**
 * Cancel appointment
 */
export const cancelAppointment = async (
  bookingId: string,
  storeId: string,
  locale = "en",
  reason?: string
): Promise<CancelAppointmentResponse | undefined> => {
  try {
    const client = new MReserveClient(locale);
    // Need to get booking first to get cancelToken
    const booking = await client.getBooking(bookingId);

    // Check if booking has cancelToken
    if (!booking.cancelToken) {
      return {
        error: {
          code: "CANCEL_TOKEN_MISSING",
          message: "Cancel token not available",
        },
      };
    }

    await client.cancelBooking(bookingId, booking.cancelToken, reason);

    return {
      data: {
        success: true,
      },
    };
  } catch (error) {
    console.error("Error canceling appointment:", error);
    return {
      error: {
        code: "CANCEL_FAILED",
        message: error instanceof Error ? error.message : "Failed to cancel appointment",
      },
    };
  }
};

/**
 * Update appointment (reschedule)
 */
export const updateAppointment = async (id: string, data: TypeBookingRequest & { serviceId?: number }, locale = "en"): Promise<BookingResponse | undefined> => {
  try {
    const serviceId = data.serviceId;

    if (!serviceId || !data.startDatetime || !data.user) {
      return {
        error: {
          code: "INVALID_REQUEST",
          message: "Missing required fields",
        },
      };
    }

    // Get booking to retrieve rescheduleToken
    const client = new MReserveClient(locale);
    const booking = await client.getBooking(id);

    if (!booking.rescheduleToken) {
      return {
        error: {
          code: "RESCHEDULE_TOKEN_MISSING",
          message: "Reschedule token not available",
        },
      };
    }

    // Parse datetime string - TypeBookingRequest only has startDatetime
    const startDatetimeStr = data.startDatetime.replace(" ", "T");
    const startDate = new Date(startDatetimeStr);

    const rescheduleData: RescheduleBookingRequest = {
      bookingId: id,
      newDate: startDate.toISOString().split("T")[0],
      newTime: startDate.toTimeString().substring(0, 5),
      firstName: data.user.firstName || "",
      lastName: data.user.lastName || "",
      email: data.user.email || "",
      phone: data.user.phoneNumber || "",
      description: data.comment || "",
      termsAccepted: true,
      mgr_customer_id: data.mgr_customer_id,
      sfcontactkey: data.sfcontactkey,
    };

    const updatedBooking = await client.rescheduleBooking(id, booking.rescheduleToken, rescheduleData);

    return {
      data: {
        success: true,
        id: updatedBooking.id,
      },
    };
  } catch (error) {
    console.error("Error updating appointment:", error);
    return {
      error: {
        code: "UPDATE_FAILED",
        message: error instanceof Error ? error.message : "Failed to update appointment",
      },
    };
  }
};

/**
 * Get public holidays for a store
 */
export const getPublicHolidays = async (storeId: string, month: number, year: number, locale = "en"): Promise<Date[]> => {
  try {
    const client = new MReserveClient(locale);
    const holidayDateStrings = await client.getPublicHolidays(storeId, month, year);
    // Convert date strings to Date objects
    const holidays = holidayDateStrings.map((dateString) => {
      // Handle date string format "YYYY-MM-DD"
      const date = new Date(dateString);
      // Set time to midnight to avoid timezone issues
      date.setHours(0, 0, 0, 0);
      return date;
    });
    return holidays;
  } catch (error) {
    console.error("Error fetching public holidays:", error);
    return [];
  }
};

/**
 * Get customer bookings by mgr_customer_id
 */
export const getCustomerBookings = async (
  mgrCustomerId: string,
  locale = "en"
): Promise<{ upcoming: BookingDetails[]; previous: BookingDetails[] } | undefined> => {
  try {
    const client = new MReserveClient(locale);
    const request: MReserveCustomerBookingsRequest = {
      mgr_customer_id: mgrCustomerId,
      include_cancelled: true,
    };

    const response = await client.getCustomerBookings(request);

    if (!response.status || !response.data) {
      return undefined;
    }

    // Map upcoming bookings
    const upcomingBookings = response.data.upcoming_bookings.map((booking) =>
      mapMReserveCustomerBookingToBookingDetails(booking)
    );

    // Map previous bookings
    const previousBookings = response.data.previous_bookings.map((booking) =>
      mapMReserveCustomerBookingToBookingDetails(booking)
    );

    return {
      upcoming: upcomingBookings,
      previous: previousBookings,
    };
  } catch (error) {
    console.error("Error fetching customer bookings:", error);
    return undefined;
  }
};
